import React, { useEffect, useState } from 'react';
import { Plus, Pencil, MoreHorizontal, X, Loader2, Save, KeyRound, Info, CheckCircle, Trash2, Building2, Activity } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { api, formatApiErrorDetail } from '../../lib/api';
import { toast } from 'sonner';

const STATUS_OPTIONS = ['Active', 'Pending', 'Suspended'];
const TYPE_OPTIONS = ['flight', 'hotel', 'bus', 'train', 'cab', 'package', 'tour', 'visa', 'cruise', 'forex', 'insurance', 'villa', 'multi'];

const STATUS_BADGE = {
  Active:    'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border-green-300 dark:border-green-700',
  Pending:   'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-300 dark:border-amber-700',
  Suspended: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border-red-300 dark:border-red-700',
};

const emptyPartner = () => ({
  name: '',
  type: 'hotel',
  commission_percent: 10,
  status: 'Pending',
  test_mode: true,
  logo_url: '',
  credentials: { cred1: '', cred2: '', cred3: '', cred4: '', cred5: '', cred6: '', base_url: '' },
});

const PartnerLogo = ({ url, name }) => {
  const [failed, setFailed] = useState(!url);
  return (
    <div className="h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
      {url && !failed ? (
        <img src={url} alt={name} className="h-full w-full object-contain p-1" onError={() => setFailed(true)} />
      ) : (
        <Building2 className="h-5 w-5 text-gray-400" />
      )}
    </div>
  );
};

const AdminTravelPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null = closed; object = open
  const [saving, setSaving] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [testing, setTesting] = useState(false);

  const handleTestConnection = async () => {
    if (!editing?.id) {
      toast.error('Save partner first, then test connection');
      return;
    }
    setTesting(true);
    try {
      const res = await api.post(`/admin/travel-partners/${editing.id}/test-connection`);
      if (res.data.ok) {
        toast.success(res.data.message);
      } else {
        toast.message(res.data.message);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const load = () => {
    setLoading(true);
    api.get('/admin/travel-partners')
      .then((r) => setPartners(r.data.partners || []))
      .catch(() => toast.error('Failed to load travel partners'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Close action menu when clicking outside
  useEffect(() => {
    const close = () => setOpenMenu(null);
    if (openMenu) {
      document.addEventListener('click', close);
      return () => document.removeEventListener('click', close);
    }
  }, [openMenu]);

  const stats = {
    total: partners.length,
    active: partners.filter((p) => p.status === 'Active').length,
    pending: partners.filter((p) => p.status === 'Pending').length,
    suspended: partners.filter((p) => p.status === 'Suspended').length,
  };

  const openCreate = () => setEditing({ ...emptyPartner(), _isNew: true });
  const openEdit = (p) => setEditing({ ...p, credentials: { cred1: '', cred2: '', cred3: '', cred4: '', cred5: '', cred6: '', base_url: '', ...(p.credentials || {}) } });

  const handleField = (key, value) => setEditing((prev) => ({ ...prev, [key]: value }));
  const handleCred = (slot, value) => setEditing((prev) => ({ ...prev, credentials: { ...prev.credentials, [slot]: value } }));

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        type: editing.type,
        commission_percent: Number(editing.commission_percent) || 0,
        status: editing.status,
        test_mode: !!editing.test_mode,
        logo_url: editing.logo_url || '',
        credentials: editing.credentials,
      };
      if (editing._isNew) {
        const res = await api.post('/admin/travel-partners', payload);
        setPartners((prev) => [res.data, ...prev]);
        toast.success('Partner created');
      } else {
        const res = await api.put(`/admin/travel-partners/${editing.id}`, payload);
        setPartners((prev) => prev.map((p) => (p.id === editing.id ? res.data : p)));
        toast.success('Partner updated');
      }
      setEditing(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (!editing?.id) {
      handleField('status', 'Active');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/admin/travel-partners/${editing.id}/activate`);
      setPartners((prev) => prev.map((p) => (p.id === editing.id ? res.data : p)));
      setEditing(null);
      toast.success('Partner activated');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Activation failed');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickStatus = async (partner, newStatus) => {
    try {
      const action = newStatus === 'Active' ? 'activate' : newStatus === 'Suspended' ? 'suspend' : null;
      const res = action
        ? await api.post(`/admin/travel-partners/${partner.id}/${action}`)
        : await api.put(`/admin/travel-partners/${partner.id}`, { status: newStatus });
      setPartners((prev) => prev.map((p) => (p.id === partner.id ? res.data : p)));
      toast.success(`Partner ${newStatus.toLowerCase()}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Update failed');
    }
  };

  const handleDelete = async (partner) => {
    if (!window.confirm(`Delete partner "${partner.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/travel-partners/${partner.id}`);
      setPartners((prev) => prev.filter((p) => p.id !== partner.id));
      toast.success('Partner deleted');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-travel-partners-page">
      {/* Header + stats */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Travel Partners</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage API suppliers (GDS, aggregators, etc.) and their credentials</p>
        </div>
        <Button onClick={openCreate} data-testid="add-partner-btn" className="bg-red-600 hover:bg-red-700">
          <Plus className="h-4 w-4 mr-1.5" /> Add Partner
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total Partners', value: stats.total, badge: null, color: 'text-gray-900 dark:text-white' },
          { label: 'Active', value: stats.active, badge: 'Active', color: 'text-green-600 dark:text-green-400' },
          { label: 'Pending', value: stats.pending, badge: 'Pending', color: 'text-amber-600 dark:text-amber-400' },
          { label: 'Suspended', value: stats.suspended, badge: 'Suspended', color: 'text-red-600 dark:text-red-400' },
        ].map((s) => (
          <Card key={s.label} className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">{s.label}</p>
              <p className={`text-3xl font-bold mt-1 ${s.color}`}>{s.value}</p>
              {s.badge && (
                <Badge variant="outline" className={`mt-2 text-[10px] ${STATUS_BADGE[s.badge]}`}>{s.badge}</Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Partners table */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center gap-2">
            <h3 className="font-bold text-gray-900 dark:text-white">All Partners</h3>
            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 hover:bg-blue-100">{partners.length}</Badge>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-red-600" /></div>
            ) : partners.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">No partners yet. Click "Add Partner" to onboard your first API supplier.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Partner Details</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Commission</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {partners.map((p) => (
                    <tr key={p.id} data-testid={`partner-row-${p.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <PartnerLogo url={p.logo_url} name={p.name} />
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">{p.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{p.type}{p.test_mode ? ' · TEST' : ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-base font-bold text-blue-600 dark:text-blue-400">{Number(p.commission_percent).toFixed(2)}%</p>
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">per booking</p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={STATUS_BADGE[p.status] || ''}>{p.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => openEdit(p)} data-testid={`edit-partner-${p.id}`} className="p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded text-blue-600 dark:text-blue-400" title="Edit">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <div className="relative" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => setOpenMenu(openMenu === p.id ? null : p.id)}
                              data-testid={`menu-partner-${p.id}`}
                              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-gray-600 dark:text-gray-300"
                              title="More"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                            {openMenu === p.id && (
                              <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 py-1">
                                {STATUS_OPTIONS.filter((s) => s !== p.status).map((s) => (
                                  <button
                                    key={s}
                                    onClick={() => { handleQuickStatus(p, s); setOpenMenu(null); }}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                                  >
                                    Mark as {s}
                                  </button>
                                ))}
                                <div className="my-1 border-t border-gray-100 dark:border-gray-800"></div>
                                <button
                                  onClick={() => { handleDelete(p); setOpenMenu(null); }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center gap-2"
                                >
                                  <Trash2 className="h-3.5 w-3.5" /> Delete partner
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
            Showing {partners.length} {partners.length === 1 ? 'entry' : 'entries'}
          </div>
        </CardContent>
      </Card>

      {/* Edit / create modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div
            className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="partner-edit-modal"
          >
            <div className="sticky top-0 bg-white dark:bg-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between z-10">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Pencil className="h-4 w-4 text-blue-600" />
                {editing._isNew ? 'Add Travel Partner' : `Edit Partner: ${editing.name}`}
              </h3>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-6">
              {/* Basic Information */}
              <section>
                <h4 className="text-base font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-2 mb-4">
                  <Info className="h-4 w-4" /> Basic Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs text-gray-600 dark:text-gray-400">Company Name</Label>
                    <Input
                      data-testid="partner-field-name"
                      value={editing.name}
                      onChange={(e) => handleField('name', e.target.value)}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 dark:text-gray-400">Commission (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      data-testid="partner-field-commission"
                      value={editing.commission_percent}
                      onChange={(e) => handleField('commission_percent', e.target.value)}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 dark:text-gray-400">Status</Label>
                    <select
                      value={editing.status}
                      onChange={(e) => handleField('status', e.target.value)}
                      data-testid="partner-field-status"
                      className="mt-1 w-full h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm"
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 dark:text-gray-400">Type</Label>
                    <select
                      value={editing.type}
                      onChange={(e) => handleField('type', e.target.value)}
                      data-testid="partner-field-type"
                      className="mt-1 w-full h-10 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm"
                    >
                      {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-xs text-gray-600 dark:text-gray-400">Logo URL (optional)</Label>
                  <Input
                    value={editing.logo_url || ''}
                    placeholder="https://logo.clearbit.com/hotelbeds.com"
                    onChange={(e) => handleField('logo_url', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </section>

              {/* Development Mode */}
              <section>
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Development Mode</h4>
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!editing.test_mode}
                    onChange={(e) => handleField('test_mode', e.target.checked)}
                    data-testid="partner-field-test-mode"
                    className="sr-only peer"
                  />
                  <span className="relative h-6 w-11 rounded-full bg-gray-300 dark:bg-gray-700 peer-checked:bg-blue-600 transition-colors">
                    <span className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">Enable Test Mode</span>
                </label>
              </section>

              {/* API Configuration */}
              <section>
                <h4 className="text-base font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-2 mb-4">
                  <KeyRound className="h-4 w-4" /> API Configuration
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i}>
                      <Label className="text-xs text-gray-600 dark:text-gray-400">API Credential {i}</Label>
                      <Input
                        type={i % 2 === 0 ? 'password' : 'text'}
                        value={editing.credentials?.[`cred${i}`] || ''}
                        data-testid={`partner-cred-${i}`}
                        onChange={(e) => handleCred(`cred${i}`, e.target.value)}
                        className="mt-1 font-mono text-xs"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <Label className="text-xs text-gray-600 dark:text-gray-400">Base URL (optional)</Label>
                  <Input
                    value={editing.credentials?.base_url || ''}
                    placeholder="https://api.test.hotelbeds.com"
                    onChange={(e) => handleCred('base_url', e.target.value)}
                    className="mt-1 font-mono text-xs"
                  />
                </div>
              </section>

              {/* Footer actions */}
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-800">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                {!editing._isNew && editing.status !== 'Active' && (
                  <Button
                    type="button"
                    onClick={handleActivate}
                    disabled={saving}
                    data-testid="activate-partner-btn"
                    className="border-2 border-green-500 bg-white dark:bg-gray-900 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20"
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" /> Activate Partner
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="save-partner-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />{editing._isNew ? 'Create Partner' : 'Update Partner'}</>}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTravelPartners;
